import axios from 'axios';
import type { AxiosResponse, InternalAxiosRequestConfig, AxiosError } from 'axios';
import toast from 'react-hot-toast';

export const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:5001/api';

// Retry configuration
const RETRY_CONFIG = {
  maxRetries: 3,
  retryDelayMs: 1500,
  retryableStatusCodes: [500, 502, 503, 504, 408, 429],
  retryableErrorCodes: [
    'ERR_CONNECTION_REFUSED',
    'ECONNREFUSED',
    'ERR_NETWORK',
    'ERR_TIMEOUT',
    'NETWORK_ERROR',
    'ECONNRESET',
    'ENOTFOUND'
  ]
};

// Flag per prevenire multipli tentativi simultanei di refresh
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value: any) => void;
  reject: (error: any) => void;
}> = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Utility to detect connection errors
const isConnectionError = (error: any): boolean => {
  return RETRY_CONFIG.retryableErrorCodes.some(errCode =>
    error?.code === errCode ||
    error?.message?.includes(errCode) ||
    error?.message?.includes('Network Error') ||
    error?.message?.includes('timeout')
  );
};

// Utility to check if an error is retryable
const isRetryableError = (error: AxiosError): boolean => {
  if (isConnectionError(error)) {
    return true;
  }

  if (error.response?.status && RETRY_CONFIG.retryableStatusCodes.includes(error.response.status)) {
    return true;
  }

  return false;
};

// Utility for delay between retries
const sleep = (ms: number): Promise<void> => {
  return new Promise(resolve => setTimeout(resolve, ms));
};

// List of public routes that DON'T require token
const PUBLIC_ROUTES = [
  '/markers/public',
  '/auth/login',
  '/auth/register',
  '/auth/verify-token',
  '/auth/refresh-token',
  '/health'
];

// List of admin routes that require admin verification
const ADMIN_ROUTES = [
  '/markers',     // Admin CRUD operations on markers
  '/admin',       // Admin panel routes
  '/stats',       // Admin statistics
  '/users'        // User management routes
];

// Utility to check if a route is public
const isPublicRoute = (url: string): boolean => {
  return PUBLIC_ROUTES.some(route => url.includes(route));
};

// Utility to check if a route requires admin permissions
const isAdminRoute = (url: string): boolean => {
  return ADMIN_ROUTES.some(route => 
    url.includes(route) && 
    !url.includes('/markers/public') // Exclude public marker routes
  );
};

// Helper to clear auth and redirect
const clearAuthAndRedirect = () => {
  console.log('🧹 Clearing auth data and redirecting to login');
  localStorage.removeItem('token');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('user');

  // Only redirect if not already on login page
  if (!window.location.pathname.includes('/login')) {
    window.location.href = '/login';
  }
};

// Create main API instance with interceptors
export const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Create separate instance for auth operations (without interceptors)
export const authApiInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor for main API
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Only add token if NOT a public route
    if (!isPublicRoute(config.url || '')) {
      const token = localStorage.getItem('token');
      if (token && config.headers) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Retry logic handler
const handleRetryLogic = async (error: AxiosError): Promise<any> => {
  const config = error.config as InternalAxiosRequestConfig & {
    _retryCount?: number;
    _isRetrying?: boolean;
  };

  if (!config._retryCount) {
    config._retryCount = 0;
  }

  if (!isRetryableError(error) || config._retryCount >= RETRY_CONFIG.maxRetries) {
    return Promise.reject(error);
  }

  if (config._isRetrying) {
    return Promise.reject(error);
  }

  config._isRetrying = true;
  config._retryCount++;

  console.log(`🔄 Retry attempt ${config._retryCount}/${RETRY_CONFIG.maxRetries} for ${config.method?.toUpperCase()} ${config.url}`);

  try {
    await sleep(RETRY_CONFIG.retryDelayMs);
    config._isRetrying = false;
    return api(config);
  } catch (retryError) {
    config._isRetrying = false;
    return Promise.reject(retryError);
  }
};

// Response interceptor - Handle 401 with refresh token + retry logic
api.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
      _retryCount?: number;
    };

    // First: Handle retry for network/server errors
    if (isRetryableError(error) && error.response?.status !== 401) {
      try {
        return await handleRetryLogic(error);
      } catch (retryError) {
        console.log(`❌ All retry attempts failed for ${originalRequest.method?.toUpperCase()} ${originalRequest.url}`);
      }
    }

    // Handle 403 Forbidden for admin routes - likely privilege escalation attempt
    if (error.response?.status === 403 && isAdminRoute(originalRequest.url || '')) {
      console.log('🚨 Access denied: User attempted to access admin resource without permission');
      clearAuthAndRedirect();
      return Promise.reject(error);
    }

    // Then: Handle 401 Unauthorized for NON-public routes
    if (error.response?.status === 401 && !originalRequest._retry && !isPublicRoute(originalRequest.url || '')) {
      console.log('🚨 Backend says: Token expired (401), attempting refresh...');

      // Controlla se abbiamo un refresh token
      const refreshToken = localStorage.getItem('refreshToken');

      if (!refreshToken) {
        console.log('❌ No refresh token available');
        clearAuthAndRedirect();
        return Promise.reject(error);
      }

      if (isRefreshing) {
        // Se stiamo già aggiornando, metti in coda questa richiesta
        console.log('⏳ Refresh in progress, queuing request...');
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${token}`;
          }
          return api(originalRequest);
        }).catch(err => {
          return Promise.reject(err);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        console.log('🔄 Refreshing token...');

        // Usa authApiInstance per evitare loop di interceptor
        const refreshResponse = await authApiInstance.post('/auth/refresh-token', {
          refreshToken: refreshToken
        });

        if (refreshResponse.data.success && refreshResponse.data.data?.token) {
          const { token: newToken, refreshToken: newRefreshToken, user } = refreshResponse.data.data;

          console.log('✅ Token refreshed successfully');

          // Aggiorna token memorizzati
          localStorage.setItem('token', newToken);
          localStorage.setItem('refreshToken', newRefreshToken);
          localStorage.setItem('user', JSON.stringify(user));

          // Aggiorna la richiesta fallita con il nuovo token
          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
          }

          // Processa le richieste in coda con il nuovo token
          processQueue(null, newToken);

          // Riprova la richiesta originale
          return api(originalRequest);
        } else {
          throw new Error('Invalid refresh response');
        }

      } catch (refreshError) {
        console.error('❌ Token refresh failed:', refreshError);

        // Processa le richieste in coda con errore
        processQueue(refreshError, null);

        // Pulisci auth data e reindirizza
        clearAuthAndRedirect();

        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    // For other errors, show toast if appropriate
    const shouldShowToast = !isPublicRoute(error.config?.url || '') &&
      !isConnectionError(error) &&
      error.response?.status !== 401 &&
      !(originalRequest._retryCount && originalRequest._retryCount > 0);

    if (shouldShowToast) {
      if (
        error.response?.data &&
        typeof error.response.data === 'object' &&
        'message' in error.response.data &&
        typeof (error.response.data as any).message === 'string'
      ) {
        toast.error((error.response.data as any).message);
      } else if (error.message && !error.message.includes('timeout')) {
        toast.error(error.message);
      }
    }

    return Promise.reject(error);
  }
);

// Add retry logic for authApiInstance
authApiInstance.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error: AxiosError) => {
    // Only retry for network errors, not for auth errors
    if (isRetryableError(error) && error.response?.status !== 401 && error.response?.status !== 403) {
      try {
        const config = error.config as InternalAxiosRequestConfig & {
          _retryCount?: number;
          _isRetrying?: boolean;
        };

        if (!config._retryCount) {
          config._retryCount = 0;
        }

        if (!config._isRetrying && config._retryCount < RETRY_CONFIG.maxRetries) {
          config._isRetrying = true;
          config._retryCount++;

          console.log(`🔄 Auth API retry attempt ${config._retryCount}/${RETRY_CONFIG.maxRetries} for ${config.method?.toUpperCase()} ${config.url}`);

          await sleep(RETRY_CONFIG.retryDelayMs);
          config._isRetrying = false;
          return authApiInstance(config);
        }
      } catch (retryError) {
        console.log(`❌ Auth API retry failed for ${error.config?.method?.toUpperCase()} ${error.config?.url}`);
      }
    }

    return Promise.reject(error);
  }
);

export default api;